import '../../globals.css';
import 'swiper/css';
import 'react-virtualized/styles.css';
import '@ungap/global-this'; // polyfill for globalThis
import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only'; // polyfill for AbortController (Chrome < 66)
import { Feature, FeatureFlagProvider } from '@kakaoent/feature-flag';
import { DehydratedState, HydrationBoundary, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Session } from 'inspector';
import { NextPage } from 'next';
import { AppProps } from 'next/app';
import getConfig from 'next/config';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useStore } from 'react-redux';
import smoothScroll from 'smoothscroll-polyfill';
import { initKpWeb } from '../app/initKpWeb.function';
import { useHandleOpenNewWindowQuery } from '../applink/useHandleOpenNewWindowQuery.hook';
import { getJwtPublicKey, initJwtPublicKey } from '../auth/jwtPublicKey';
import { SESSION_STORAGE_INITIAL_LOAD_MARKED, SESSION_STORAGE_SPA_INITIAL_PATH } from '../common/constant';
import { AppInitializer } from '../common/containers/AppInitializer';
import { getEnv, initializePagewebEnv } from '../common/env/env';
import { ServerEnv } from '../common/env/ServerEnv.enum';
import { requestContext } from '../common/requestContext';
import { DisplaySelectedStrategy } from '../common/requestContext/DisplaySelectedStrategy.enum';
import { useHasMounted } from '../common/shared/hooks/useHasMounted';
import { getOpenWebviewRedirectPagewebUrl } from '../common/shared/scheme/getOpenWebviewRedirectPagewebUrl.function';
import { InvalidPathError } from '../common/shared/scheme/InvalidPathError.class';
import { MalformedPathError } from '../common/shared/scheme/MalformedPathError.class';
import { RedirectUrlError } from '../common/shared/scheme/RedirectUrlError.class';
import { IS_CLIENT_SIDE } from '../common/shared/utils/constants';
import { getV1RedirectPagewebUrl } from '../common/shared/utils/getV1RedirectPagewebUrl.function';
import { isTrue } from '../common/shared/utils/isTrue';
import { DehydratedQueryOptions } from '../common/type';
import useTypedDispatch from '../common/useTypedDispatch';
import { setIsIpxMediaMuted, setIsIpxMediaNetworkUsingAgreed } from '../config/configSlice';
import { LOCAL_STORAGE_IPX_MEDIA_NETWORK_USING_AGREED_KEY, SESSION_STORAGE_IPX_MEDIA_MUTED_KEY } from '../config/constants';
import { ColorModeListener } from '../developer/ColorModeListener';
import { DeveloperBlock } from '../developer/DeveloperBlock';
import { HydrateOption } from '../developer/HydrateOption';
import { removeSimpleDialog } from '../dialog/simpleDialogHandler';
import { useAppLinkDialog } from '../dialog/useAppLinkDialog';
import { ApiErrorBoundary } from '../errors/ApiErrorBoundary.class';
import { GlobalErrorBoundary } from '../errors/GlobalErrorBoundary.class';
import { getKakaopageFeature, initializeKakaopageFeature } from '../feature/variable';
import { CertType } from '../graphql/generated/types';
import { removeImagePopup } from '../imagePopup/imagePopupHandler';
import { initServerLogger } from '../log/logger';
import { initSentryClient } from '../logging/initSentryClient.function';
import { trackEvent } from '../logging/trackEvent';
import { createQueryClient } from '../reactQuery/createQueryClient';
import { useSelfCertCompletedEvent } from '../selfCert/useSelfCertComplete.hook';
import { AppStore, nextReduxWrapper } from '../state/store';
import { decryptVariables } from '../variables/decryptVariables.function';
import { initializeVariables } from '../variables/initializeVariables.function';
require('polyfill-object.fromentries');
require('../common/intersection-observer-polyfill');
type Page<P = unknown> = NextPage<P> & {
  getLayout?: (page: ReactNode) => ReactNode;
};
type Props = AppProps<{
  dehydratedState: DehydratedState;
  dehydratedQueryOptions?: DehydratedQueryOptions;
  session: Session;
}> & {
  Component: Page;
  ecvars?: string;
  feature: Feature;
  campaignQueries?: {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
  };
};

// next.js 에선 서버 파일도 번들링하기 때문에 express 에서 설정한 env 변수에 접근하지 못합니다. 그래서 serverSideProps 를 통해 환경변수를 건네줍니다
const {
  serverRuntimeConfig
} = getConfig();
initializePagewebEnv(serverRuntimeConfig.env);
if (IS_CLIENT_SIDE && window.ecvars) {
  const decryptedVariable = decryptVariables(window.ecvars);
  decryptedVariable && initializeVariables(decryptedVariable);
  delete window.ecvars;
}
function App({
  Component,
  pageProps,
  feature,
  campaignQueries
}: Props) {
  if (IS_CLIENT_SIDE) {
    // 클라이언트 사이드에서는 feature 초기화를 한번만 하도록 합니다. ()
    !getKakaopageFeature() && initializeKakaopageFeature(feature);
  }
  const getLayout = Component.getLayout || (page => page);
  // 환경 변수 가져온 직후에 센트리를 설정합니다.
  IS_CLIENT_SIDE && getEnv().isSentryOn && initSentryClient(getEnv());
  IS_CLIENT_SIDE && smoothScroll.polyfill(); //smooth 스크롤을 위한 폴리필을 적용합니다.
  const store = useStore();
  IS_CLIENT_SIDE && initKpWeb(store as AppStore);
  const router = useRouter();
  const handleUrlRedirect = useCallback((url: string) => {
    if (IS_CLIENT_SIDE) {
      const redirectUrl = getV1RedirectPagewebUrl({
        v1Url: url,
        linkOrigin: getEnv().linkOrigin
      });
      if (redirectUrl) {
        router.events.emit('routeChangeError', new RedirectUrlError(redirectUrl.fullUrl));
        throw 'Abort current route';
      }
      try {
        const redirectUrl = getOpenWebviewRedirectPagewebUrl({
          url,
          webOrigin: getEnv().webOrigin
        });
        if (redirectUrl) {
          router.events.emit('routeChangeError', new RedirectUrlError(redirectUrl.fullUrl));
          throw 'Abort current route';
        }
      } catch (e) {
        if (e instanceof MalformedPathError) {
          router.events.emit('routeChangeError', new RedirectUrlError('/error/malformed-path'));
          throw 'Abort current route';
        } else if (e instanceof InvalidPathError) {
          // invalidPathError라면 open/webview 형태의 URL이 아니라는 의미이므로 bypass 합니다.
        } else {
          throw e;
        }
      }
    }
  }, [router]);
  const handleRedirectUrlError = useCallback((error: Error) => {
    if (error instanceof RedirectUrlError) {
      router.push(error.message, error.message);
    }
  }, [router]);
  useSelfCertCompletedEvent();
  const handleRemoveSimpleDialog = useCallback(() => removeSimpleDialog(), []);
  useEffect(() => {
    // SSG 페이지의 경우, 처음 hydrate 할 때에는 쿼리 정보가 비어서 들어온 후
    // hydrate가 완료된 후에 쿼리 정보를 업데이트 해주기 때문에 routeChangeStart 이벤트가 두 번 발생하거나 처음 페이지 진입 시 routeChangeStart 이벤트가 발생합니다.
    // 이를 방지하기 위해 router.isReady가 true일 때 이벤트 핸들러를 연결해줍니다.
    if (router.isReady) {
      router.events.on('routeChangeStart', removeImagePopup);
      router.events.on('routeChangeStart', handleRemoveSimpleDialog);
      router.events.on('routeChangeStart', handleUrlRedirect);
      router.events.on('routeChangeError', handleRedirectUrlError);
    }
    return () => {
      router.events.off('routeChangeError', handleRedirectUrlError);
      router.events.off('routeChangeStart', handleUrlRedirect);
      router.events.off('routeChangeStart', handleRemoveSimpleDialog);
      router.events.off('routeChangeStart', removeImagePopup);
    };
  }, [handleRedirectUrlError, handleRemoveSimpleDialog, handleUrlRedirect, router]);
  useEffect(() => {
    // 'spa_initial_load_marked' 플래그가 없으면 이 세션에서 SPA가 처음 로드된 것입니다.
    if (!sessionStorage.getItem(SESSION_STORAGE_INITIAL_LOAD_MARKED)) {
      // 현재 경로를 SPA의 '루트'로 마킹
      // Next.js 라우터는 history.pushState/replaceState를 내부적으로 관리하므로
      // 우리가 직접 history.replaceState를 호출하지는 않습니다.
      // 대신 세션 스토리지에 플래그를 저장합니다.
      sessionStorage.setItem(SESSION_STORAGE_INITIAL_LOAD_MARKED, 'true');
      sessionStorage.setItem(SESSION_STORAGE_SPA_INITIAL_PATH, router.asPath); // 최초 진입 경로 저장
    }
  }, [router.asPath]); // router.asPath가 변경될 때마다 실행되도록 할 수도 있지만, 최초 로드에 집중

  useHandleOpenNewWindowQuery();
  const dispatch = useTypedDispatch();
  // 최초에 한번 ipx 미디어 세팅값을 세팅합니다.
  useEffect(() => {
    // 음소거값은 세션동안만 유효하므로 session storage로 관리됩니다.
    const isIpxMediaMuted = sessionStorage?.getItem(SESSION_STORAGE_IPX_MEDIA_MUTED_KEY);
    if (isIpxMediaMuted) {
      dispatch(setIsIpxMediaMuted(isTrue(isIpxMediaMuted)));
    }

    // 데이터 사용동의 정보는 반영구적(localStorage)로 관리됩니다.
    const isIpxMediaNetworkUsingAgreed = localStorage?.getItem(LOCAL_STORAGE_IPX_MEDIA_NETWORK_USING_AGREED_KEY);
    if (isIpxMediaNetworkUsingAgreed) {
      dispatch(setIsIpxMediaNetworkUsingAgreed(isTrue(isIpxMediaNetworkUsingAgreed)));
    }
  }, [dispatch]);
  const onOrientationChange = useCallback(async () => {
    // iPad 앱의 웹뷰 내에서 디바이스가 회전했을 때, resize 이벤트가 회전 시작하는 시점에 발생하므로
    // 그 순간의 window의 innerWidth와 innerHeight 값이 회전이 끝나는 시점의 값과 다름.
    // 회전이 끝나는 시점을 정확히 알 수가 없으므로, 적절한 시간 이후 다시 윈도우에 resize 이벤트를 발생시켜 해결함.
    // (iOS 사파리에서는 resize 시점이 문제가 없어보이는데, iOS 서드파티 앱 내에 사용되는 웹뷰에서는 동일한 문제가 있음.)
    // 참고: https://stackoverflow.com/questions/12452349/mobile-viewport-height-after-orientation-change
    await orientationChanged();
    window.dispatchEvent(new Event('resize'));

    // iOS14 미만 기기에서는 landscape에서 portrait로 바뀔 때 scroll y offset이 더 크게 변하는데
    // 스크롤이 바닥에 닿는 상황보다 더 큰 값이 될 수 있으므로, 이때 바닥에 붙도록 조정함
    // iOS14부터는 개선된 듯 하나, 적용되도 딱히 문제되지 않으므로 iOS 버전에 따른 분기처리 하지 않음
    const scrollBottomOffset = Math.max(0, window.document.body.scrollHeight - window.innerHeight);
    if (window.scrollY > scrollBottomOffset) {
      window.scrollTo(0, scrollBottomOffset);
    }
  }, []);
  useEffect(() => {
    if (requestContext.requestInfo.display.isIos) {
      // 디바이스 회전과 관련한 처리를 위해 window에 이벤트리스너 추가함
      // onOrientationChange 함수의 주석 참고할 것
      window.addEventListener('orientationchange', onOrientationChange);
    }
  }, [onOrientationChange]);

  // 유입경로 로깅. t_xxx 는 티아라에서 알아서 남겨주지만 utm_xxx 는 자동으로 남기고 있지 않아 추가합니다(데이터팀 요청)
  useEffect(() => {
    if (!requestContext.requestInfo.display.isWebView && campaignQueries) {
      trackEvent({
        actionType: 'Event',
        actionName: '유입경로',
        section: 'non_screen_event',
        page: 'non_screen_event',
        customProps: {
          user_action: null,
          t_src: campaignQueries.utm_source,
          t_ch: campaignQueries.utm_medium,
          t_obj: campaignQueries.utm_campaign
        }
      });
    }
  }, [campaignQueries]);
  const hasMounted = useHasMounted();
  const [queryClient] = useState(createQueryClient);
  const {
    AppLinkDialog,
    isOpen,
    closeDialog,
    linkUrl
  } = useAppLinkDialog();
  const showVieweportFitCover = router.query.viewtype === 'full' || VIEWPORT_COVER_PATHS.some(prefix => router.pathname.startsWith(prefix));
  return <>
      <Head>
        <meta name="viewport" content={`width=device-width, initial-scale=1, minimum-scale=1.0, maximum-scale=1.0, shrink-to-fit=no${showVieweportFitCover ? ',viewport-fit=cover' : ''}`} />
      </Head>
      <QueryClientProvider client={queryClient}>
        <HydrationBoundary state={pageProps.dehydratedState}>
          <HydrateOption dehydratedOptions={pageProps.dehydratedQueryOptions}>
            <FeatureFlagProvider feature={feature}>
              <AppInitializer>
                <ColorModeListener />
                {/* Static 페이지가 빌드시점에 생성되어 Fix되는 경우에는 런타임과 다른 EnvName으로 빌드될 가능성이 있고, 그렇게 되면 빌드시점에 만든 정보와 런타임의 데이터가 달라서 화면이 깨지게 됩니다.
                 따라서 빌드 시점과 런타임에서 모두 DeveloperBlock이 존재하지 않고, CSR에서 마운트 되었을 때에만 존재하도록 수정합니다.
                 */}
                {getEnv().envName !== ServerEnv.Real && hasMounted && <DeveloperBlock />}
                <GlobalErrorBoundary>
                  <ApiErrorBoundary>
                    {getLayout(<Component {...pageProps} />)}
                  </ApiErrorBoundary>
                </GlobalErrorBoundary>
                {getEnv().isLocal && <ReactQueryDevtools buttonPosition="bottom-left" initialIsOpen={false} />}
                <AppLinkDialog isOpen={isOpen} onClickAppLinkButton={() => window.location.assign(linkUrl)} onClickMobileWebButton={closeDialog} onClose={closeDialog} isMobile={requestContext?.requestInfo?.display?.isMobile} />
              </AppInitializer>
            </FeatureFlagProvider>
          </HydrateOption>
        </HydrationBoundary>
      </QueryClientProvider>
      <style jsx global>{`
        * {
          ${requestContext.requestInfo.display.isWebView ? '-webkit-tap-highlight-color: transparent;' : ''}
        }
      `}</style>
    </>;
}
function orientationChanged() {
  const timeout = 100;
  return new Promise<void>(resolve => {
    const go = (i: number, baseSize: {
      innerWidth: number;
      innerHeight: number;
    }) => {
      window.innerWidth !== baseSize.innerWidth && window.innerHeight !== baseSize.innerHeight || i >= timeout ? resolve() : window.requestAnimationFrame(() => go(i + 1, baseSize));
    };
    const {
      innerWidth,
      innerHeight
    } = window;
    go(0, {
      innerWidth,
      innerHeight
    });
  });
}

// 웹뷰 viewport=cover 사용 + 웹에서 고정된 헤더를 노출하는 페이지
const VIEWPORT_COVER_PATHS = ['/signup', '/login', '/global-policy', '/account/find-password', '/user/agreement', '/helix/landing'];
App.getInitialProps = async ({
  Component,
  ctx
}: any) => {
  if (!IS_CLIENT_SIDE) {
    // @ts-ignore variables 는 무조건 서버사이드에서 전달한다고 가정
    const variables = ctx?.req?.variables;
    ctx?.req?.serverLogger && initServerLogger({
      serverLogger: ctx?.req?.serverLogger
    });
    if (variables) {
      initializeVariables(variables);
      initializeKakaopageFeature(variables.feature);
    } else {
      requestContext.requestInfo = {
        requestId: 'dummy',
        deviceId: 'dummy',
        identifierId: 'dummy',
        registerDeviceModel: 'dummy',
        deviceModel: 'dummy',
        browserName: 'dummy',
        browserVersion: 'dummy',
        osName: 'dummy',
        osVersion: 'dummy',
        bypassSelfAuth: false,
        forceKakaobankCert: false,
        display: {
          isMobile: isTrue(true),
          isWebView: isTrue(false),
          isAndroid: false,
          isIos: false,
          strategy: DisplaySelectedStrategy.None,
          isKakaotalk: false,
          isBot: false
        },
        isGlobal: false,
        logging: true,
        isSamsungBrowser: false,
        lang: {
          deviceLang: 'ko',
          serviceLang: 'ko'
        },
        appInit: {
          certType: CertType.Nice,
          geoIp: 'KR'
        }
      };
    }
    !getJwtPublicKey() && (await initJwtPublicKey());
  }
  const pageProps = Component.getInitialProps ? await Component.getInitialProps({
    ...ctx
  }) : {};
  const {
    utm_source,
    utm_medium,
    utm_campaign
  } = !IS_CLIENT_SIDE && ctx?.req?.query || {};
  return {
    pageProps,
    feature: ctx?.req?.variables?.feature,
    ...((utm_source || utm_medium || utm_campaign) && {
      campaignQueries: {
        utm_source,
        utm_medium,
        utm_campaign
      }
    })
  };
};
export default nextReduxWrapper.withRedux(App);